import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faExternalLinkAlt,
  faFileAlt,
  faXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import api, { API_URL } from '../services/api';
import { apiUrl, safeBlob } from '../utils/documents';
import '../styles/modal-shared.css';

function DocumentViewerModal({ show, onHide, document: documentData }) {
  const [loading, setLoading]           = useState(true);
  const [isImage, setIsImage]           = useState(false);
  const [isPdf, setIsPdf]               = useState(false);
  const [documentUrl, setDocumentUrl]   = useState('');
  const [fileData, setFileData]         = useState(null);
  const [error, setError]               = useState(null);
  const [useObjectTag, setUseObjectTag] = useState(false);
  const [key, setKey]                   = useState(0);

  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);
      setDocumentUrl('');
      setFileData(null);
      setKey(k => k + 1);
    }
  }, [show]);

  useEffect(() => {
    if (!documentData) return;
    try {
      setKey(k => k + 1);
      const fileName = documentData.fileName?.toLowerCase() || '';
      const isPdfFile = fileName.endsWith('.pdf')
        || documentData.fileData?.includes('application/pdf')
        || documentData.type?.includes('pdf');
      const isImgFile = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName)
        || documentData.fileData?.includes('image/');
      setIsImage(isImgFile);
      setIsPdf(isPdfFile);

      if (documentData.fileData) {
        setFileData(documentData.fileData);
        setLoading(false);
      } else if (documentData.blobUrl) {
        fetchAndCreateBlobUrl(documentData.blobUrl, isPdfFile);
      } else if (documentData.url) {
        setDocumentUrl(documentData.url);
        setLoading(false);
      } else if (documentData.fileName) {
        const apiUrl = `${API_URL}/api/bols/documents/${documentData.fileName}`;
        if (isPdfFile) {
          fetchAndCreateBlobUrl(apiUrl, true);
        } else {
          setDocumentUrl(apiUrl);
          setLoading(false);
        }
      } else {
        throw new Error('No valid document source');
      }
      setError(null);
    } catch (err) {
      setError('Failed to prepare document for viewing');
      setLoading(false);
    }
  }, [documentData]);

  const fetchAndCreateBlobUrl = async (url, isPdfFile) => {
    try {
      // Only render-safe types survive into the object URL — see utils/documents.
      if (url.startsWith('blob:') || url.startsWith('http')) {
        // An object URL this page made, or this API. Anything else — an old
        // public storage link, say — is not fetched.
        if (!url.startsWith('blob:') && !apiUrl(url)) {
          throw new Error('Not an API document URL');
        }
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const responseBlob = await res.blob();
        const blob = safeBlob(responseBlob, isPdfFile ? 'application/pdf' : res.headers.get('content-type'));
        setDocumentUrl(URL.createObjectURL(blob));
        setUseObjectTag(isPdfFile);
      } else {
        const res = await api.get(url, { responseType: 'blob' });
        const blob = safeBlob(res.data, isPdfFile ? 'application/pdf' : res.headers['content-type']);
        setDocumentUrl(URL.createObjectURL(blob));
        setUseObjectTag(isPdfFile);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load document');
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => { if (documentUrl?.startsWith('blob:')) URL.revokeObjectURL(documentUrl); };
  }, [documentUrl]);

  const downloadDocument = () => {
    const src = fileData || documentUrl;
    if (!src) return;
    const link = Object.assign(document.createElement('a'), {
      href: src,
      download: documentData?.fileName || 'document',
    });
    link.click();
  };

  const openInNewTab = () => {
    const src = fileData || documentUrl;
    if (src) window.open(src, '_blank', 'noopener,noreferrer');
  };

  if (!documentData) return null;

  const title = documentData.descr || documentData.fileName || 'Document';
  const src   = fileData || documentUrl;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="detail-modal doc-viewer-modal"
    >
      {/* ── HEADER ── */}
      <Modal.Header closeButton>
        <div className="modal-title-row">
          <div className="modal-header-icon">
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div className="modal-header-text">
            <h5>{title}</h5>
            {documentData.fileName && title !== documentData.fileName && (
              <p className="modal-subtitle">{documentData.fileName}</p>
            )}
          </div>
        </div>
      </Modal.Header>

      {/* ── BODY — full-height viewer ── */}
      <Modal.Body className="doc-viewer-body" style={{ padding: 0 }}>
        {/* macOS-style chrome bar */}
        <div className="doc-viewer-chrome">
          <div className="doc-viewer-chrome-dot" style={{ background: '#ef4444' }} />
          <div className="doc-viewer-chrome-dot" style={{ background: '#f59e0b' }} />
          <div className="doc-viewer-chrome-dot" style={{ background: '#10b981' }} />
          <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>
            {title}
          </span>
        </div>

        <div className="doc-viewer-frame-wrap">
          {loading && (
            <div className="doc-viewer-loading">
              <div className="doc-viewer-spinner" />
              <span>Loading document…</span>
            </div>
          )}

          {error && !loading && (
            <div className="doc-viewer-error">
              <div className="doc-viewer-error-icon">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </div>
              <p style={{ margin: 0, maxWidth: 280 }}>{error}</p>
              {src && (
                <button className="detail-btn detail-btn--ghost" onClick={openInNewTab} style={{ marginTop: 8 }}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  Open in New Tab
                </button>
              )}
            </div>
          )}

          {!loading && !error && isImage && (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#1e293b' }}>
              <img
                src={src}
                alt={title}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
                onError={() => setError('Failed to load image')}
              />
            </div>
          )}

          {!loading && !error && !isImage && (
            useObjectTag ? (
              <object
                key={key}
                data={documentUrl}
                type="application/pdf"
                className="doc-viewer-frame"
              >
                <div className="doc-viewer-error">
                  <p>PDF preview unavailable.</p>
                  <button className="detail-btn detail-btn--ghost" onClick={openInNewTab}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    Open in New Tab
                  </button>
                </div>
              </object>
            ) : (
              <iframe
                key={key}
                src={documentUrl}
                title={title}
                className="doc-viewer-frame"
              />
            )
          )}
        </div>
      </Modal.Body>

      {/* ── FOOTER ── */}
      <Modal.Footer>
        <button
          className="detail-btn detail-btn--ghost"
          onClick={openInNewTab}
          disabled={!src}
          style={{ marginRight: 'auto' }}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
          Open in New Tab
        </button>
        <button className="detail-btn detail-btn--secondary" onClick={onHide}>
          <FontAwesomeIcon icon={faXmark} />
          Close
        </button>
        <button
          className="detail-btn detail-btn--primary"
          onClick={downloadDocument}
          disabled={!src}
        >
          <FontAwesomeIcon icon={faDownload} />
          Download
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default DocumentViewerModal;
